/**
 * The space the page really has. The width is the layout viewport's; the height the visual viewport's when the
 * browser has one (on a phone the on-screen keyboard shrinks it, not the window). A pinch-zoom shrinks the visual
 * viewport too, and that must not reflow the page: while zoomed in, the last size holds. Rounded, so a sub-pixel
 * jitter never re-renders the page. ``--app-height`` on the root carries the height to CSS.
 */
import { useSyncExternalStore } from 'react';
import type { Viewport } from '../shell/page-layout';

const ZOOMED = 1.01;

function read(previous: Viewport | null): Viewport {
  if (typeof window === 'undefined') return { width: 1080, height: 1920 };
  const v = window.visualViewport;
  if (previous && v && v.scale > ZOOMED) return previous;
  const width = document.documentElement.clientWidth || window.innerWidth;
  return { width: Math.round(width), height: Math.round(v?.height ?? window.innerHeight) };
}

function publish(viewport: Viewport): void {
  if (typeof document !== 'undefined') document.documentElement.style.setProperty('--app-height', `${viewport.height}px`);
}

let current = read(null);
publish(current);

function subscribe(onChange: () => void): () => void {
  const update = () => {
    const next = read(current);
    if (next.width === current.width && next.height === current.height) return;
    current = next;
    publish(current);
    onChange();
  };
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  window.visualViewport?.addEventListener('resize', update);
  return () => {
    window.removeEventListener('resize', update);
    window.removeEventListener('orientationchange', update);
    window.visualViewport?.removeEventListener('resize', update);
  };
}

export function useViewport(): Viewport {
  return useSyncExternalStore(subscribe, () => current, () => current);
}
